import { useMemo } from 'react';
import {
  useInfiniteQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import type {
  AssistantMessage,
  AssistantMessagePage,
} from '@xtreed/academy-shared';
import { assistantService } from '@/services/assistant.service';
import { assistantKeys } from './assistantKeys';

export type MessagesCache = InfiniteData<AssistantMessagePage, string | null>;

export function useAssistantMessages(conversationId: string | null) {
  const query = useInfiniteQuery({
    queryKey: assistantKeys.messages(conversationId ?? 'none'),
    queryFn: ({ pageParam }) =>
      assistantService.messages(conversationId as string, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.nextCursor,
    enabled: Boolean(conversationId),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const messages = useMemo<AssistantMessage[]>(
    () =>
      query.data
        ? [...query.data.pages].reverse().flatMap((page) => page.items)
        : [],
    [query.data],
  );

  return { ...query, messages };
}

export function useUpdateMessagesCache() {
  const queryClient = useQueryClient();

  return (
    conversationId: string,
    update: (messages: AssistantMessage[]) => AssistantMessage[],
  ) => {
    queryClient.setQueryData<MessagesCache>(
      assistantKeys.messages(conversationId),
      (current) => {
        const base: MessagesCache = current ?? {
          pages: [{ items: [], nextCursor: null }],
          pageParams: [null],
        };
        const [newest, ...older] = base.pages;
        return {
          ...base,
          pages: [{ ...newest, items: update(newest.items) }, ...older],
        };
      },
    );
  };
}
